import { CartEvent, ProductEvent, PromoEvent, WishlistButtonEvent } from '../enums/Tracking';
import type { OverlayInteraction, OverlayStatus } from '../types/tracking';
import type { DyTrackingParams } from './campaignEventData';

interface BaseProps {
  event: string;
}

interface BaseImageElementProps extends DyTrackingParams {
  element: 'image';
  type: string;
  title: string;
}

interface ImageElementProps extends BaseImageElementProps {
  list_position: number;
  list_length?: number;
  list_item?: string;
}

export interface MultipleImagesElementItem {
  list_item: string;
  list_position: number;
}

interface MultipleImagesElementProps extends BaseProps, BaseImageElementProps {
  list_length: number;
  items: MultipleImagesElementItem[];
}

interface LinkElementProps extends DyTrackingParams {
  element: 'link';
  link_text: string;
  link_url: string;
  title?: string;
  type?: string;
}

export interface ButtonElementProps extends BaseProps, DyTrackingParams {
  element: 'button';
  button_text: string;
}

export interface DrawerElementProps {
  element: string;
  overlay: OverlayStatus;
  interaction?: OverlayInteraction;
  list_position?: number;
  list_length?: number;
}

type EventGA4Props =
  | ImageElementProps
  | MultipleImagesElementProps
  | LinkElementProps
  | ButtonElementProps
  | DrawerElementProps;

type EventData<T = EventGA4Props> = BaseProps & T;

export const enum EventDataField {
  EventData = 'event_data',
  ECommerce = 'ecommerce',
}

const EcommerceEvents = [
  ProductEvent.View,
  ProductEvent.Click,
  ProductEvent.DetailView,
  CartEvent.AddToCart,
  CartEvent.RemoveFromCart,
  PromoEvent.PromoView,
  PromoEvent.PromoClick,
  WishlistButtonEvent.AddToWishlist,
  'begin_checkout',
  'add_shipping_info',
  'add_payment_info',
];

export const dispatchTrackingEventGA4 = <T>(data: EventData<T>) => {
  if (Object.keys(data).length > 0) {
    const { event, ...eventData } = data;

    const isEcommerceEvent = EcommerceEvents.includes(event);
    const eventDataField = isEcommerceEvent ? EventDataField.ECommerce : EventDataField.EventData;
    const dataLayer = (window.dataLayer = window.dataLayer || []);

    if (isEcommerceEvent) {
      dataLayer.push({ [EventDataField.ECommerce]: null });
    }
    dataLayer.push({ event, [eventDataField]: eventData, _clear: true });
  }
};
